import React from 'react';
import styled from 'styled-components';

const GaugeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-4);
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  min-width: 200px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: var(--shadow-sm);
    border-color: var(--color-secondary);
  }
`;

const GaugeContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: var(--spacing-3);
`;

const GaugeSVG = styled.svg`
  transform: rotate(-90deg);
  width: 120px;
  height: 120px;
`;

const GaugeTrack = styled.circle`
  fill: none;
  stroke: #f3f4f6;
  stroke-width: 8;
`;

const GaugeProgress = styled.circle<{ progress: number; color?: string }>`
  fill: none;
  stroke: ${props => props.color || 'var(--color-secondary)'};
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: ${2 * Math.PI * 50};
  stroke-dashoffset: ${props => 2 * Math.PI * 50 * (1 - props.progress / 100)};
  transition: stroke-dashoffset 0.8s ease-in-out;
`;

const GaugeText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  
  .percentage {
    font-size: var(--font-size-xl);
    font-weight: 700;
    color: var(--color-secondary);
    line-height: 1;
  }
  
  .label {
    font-size: var(--font-size-xs);
    color: var(--color-text-light);
    margin-top: 2px;
  }
`;

const GaugeTitle = styled.h4`
  margin: 0 0 var(--spacing-2) 0;
  font-size: var(--font-size-sm);
  color: var(--color-text);
  text-align: center;
  font-weight: 500;
`;

const GaugeStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-1);
  
  .fraction {
    font-size: var(--font-size-sm);
    color: var(--color-text);
    font-weight: 500;
  }
  
  .description {
    font-size: var(--font-size-xs);
    color: var(--color-text-light);
    text-align: center;
    line-height: 1.3;
  }
`;

interface SEOProgressGaugeProps {
  title: string;
  completed: number;
  total: number;
  description?: string;
  color?: string;
  icon?: string;
}

const SEOProgressGauge: React.FC<SEOProgressGaugeProps> = ({
  title,
  completed,
  total,
  description,
  color,
  icon
}) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  const getProgressColor = () => {
    if (color) return color;
    if (percentage >= 80) return '#10b981'; // green
    if (percentage >= 50) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  return (
    <GaugeWrapper>
      <GaugeTitle>
        {icon && <span style={{ marginRight: '8px' }}>{icon}</span>}
        {title}
      </GaugeTitle>
      
      <GaugeContainer>
        <GaugeSVG>
          <GaugeTrack
            cx="60"
            cy="60"
            r="50"
          />
          <GaugeProgress
            cx="60"
            cy="60"
            r="50"
            progress={percentage}
            color={getProgressColor()}
          />
        </GaugeSVG>
        
        <GaugeText>
          <div className="percentage">{percentage}%</div>
          <div className="label">komplett</div>
        </GaugeText>
      </GaugeContainer>
      
      <GaugeStats>
        <div className="fraction">{completed} av {total}</div>
        {description && <div className="description">{description}</div>}
      </GaugeStats>
    </GaugeWrapper>
  );
};

export default SEOProgressGauge;